import { useMemo } from 'react';
import { IconX } from '@tabler/icons-react';
import { Button } from '@hana/ui';
import { defOf, leftShort, stepOf } from './logic';
import type { ServiceTask, SvcCode, SvcStatus } from './types';
import s from './services.module.css';
import { useT } from '@hana/i18n';
import './svcdock.i18n';

// Bảng phải 372 của Mint-Service ở khổ 1920: "Việc theo dịch vụ" (nhóm theo dịch vụ) · "Thông báo".
export type DockTab = 'task' | 'noti';
const CODES: readonly SvcCode[] = ['CCF', 'COO', 'FUM', 'PTF', 'SCY', 'ISR', 'DDL', 'TRF'];
const ST_DOT: Record<SvcStatus, string> = { New: 'var(--d-amber)', Processing: 'var(--d-blue)', 'Chờ bên ngoài': 'var(--d-violet)', Finish: 'var(--d-green)' };
const NOTIS = [
  { k: 'Tờ khai 306124577210 luồng Vàng · SL260638510 đang trễ 2h', when: '10:05 · cần báo khách và lịch kiểm hoá', c: 'var(--err)', unread: true },
  { k: 'EMDEES đã nhận C/O nháp EUR.1 · chờ khách duyệt', when: '09:48', c: 'var(--d-violet)', unread: true },
  { k: 'FUM2612838 còn 3 giờ làm việc tới hạn', when: '09:30', c: 'var(--d-amber)', unread: false },
  { k: 'Quy tắc giao việc: NHIDTT nghỉ phép 24/Oct → việc mới giao HOATTN', when: 'Hôm qua 17:20', c: 'var(--d-blue)', unread: false },
  { k: 'SCY2612815 trễ hạn 1h · đã leo thang [OPS-HEAD] PHONGNT', when: 'Hôm qua 13:05', c: 'var(--err)', unread: false },
] as const;

export function ServiceDock({ rows, me, tab, onTab, onClose }: { rows: readonly ServiceTask[]; me: string; tab: DockTab; onTab: (t: DockTab) => void; onClose: () => void }) {
  const tr = useT();
  const groups = useMemo(
    () =>
      CODES.map((code) => {
        const items = rows.filter((r) => r.code === code && r.status !== 'Finish' && (r.who === me || r.left < 0));
        return { code, n: items.length, items: items.slice(0, 2) };
      }).filter((g) => g.n),
    [rows, me],
  );
  return (
    <div className={s.dock}>
      <div className={s.dkBar}>
        <div className={s.dkTabs} role="tablist" aria-label={tr('svd.panel')}>
          {([['task', tr('svd.tasks')], ['noti', tr('svd.noti')]] as const).map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => onTab(key)}>
              {label}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" aria-label={tr('svd.close')} title={tr('svd.close')} icon={<IconX size={17} aria-hidden="true" />} onClick={onClose} />
      </div>
      {tab === 'task' &&
        groups.map((g) => (
          <div key={g.code}>
            <div className={s.dkGrp}>
              <span>{g.code} · {defOf(g.code).name}</span>
              <span className={s.lb}>{g.n}</span>
            </div>
            {g.items.map((r) => {
              const tone = r.left < 0 ? s.err : r.left <= 2 ? s.warnTx : s.lb;
              return (
                <div key={r.id} className={s.dkItem}>
                  <span className={s.dkDot} style={{ background: ST_DOT[r.status] }} aria-hidden="true" />
                  <div>
                    <div>{stepOf(r).name.replace(' · Gấp', '')} · {r.id}</div>
                    <div className={s.lb}>
                      <span className={s.job}>{r.job}</span> · <span className={tone}>{leftShort(r)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      {tab === 'noti' &&
        NOTIS.map((n) => (
          <div key={n.k} className={s.dkNoti}>
            <span className={s.dkDot} style={{ background: n.c }} aria-hidden="true" />
            <div>
              <div style={{ fontWeight: n.unread ? 600 : 450 }}>{n.k}</div>
              <div className={s.lb}>{n.when}</div>
            </div>
          </div>
        ))}
    </div>
  );
}
